<template>
  <div class="news-detail-page">
    <Top />

    <section class="page-hero" aria-label="News detail header">
      <div class="container page-hero-inner">
        <h1 class="page-hero-title">{{ pageHeading }}</h1>
        <p v-if="heroSeoDescription" class="hero-seo-desc">{{ heroSeoDescription }}</p>
      </div>
    </section>

    <section class="detail-wrap" aria-label="News article">
      <div class="container detail-inner">
        <div v-if="loading" class="state-msg">Loading…</div>
        <div v-else-if="errorMsg" class="state-msg state-err">{{ errorMsg }}</div>
        <article v-else-if="article" class="detail-article">
          <div v-if="coverSrc" class="detail-cover">
            <img class="cover-img" :src="coverSrc" :alt="article.title || ''" loading="lazy" />
          </div>
          <header class="detail-header">
            <h2 class="detail-title">{{ article.title }}</h2>
            <div v-if="metaDate || article.publisher" class="detail-meta">
              <span v-if="metaDate">{{ metaDate }}</span>
              <span v-if="metaDate && article.publisher" class="meta-sep" aria-hidden="true">·</span>
              <span v-if="article.publisher">{{ article.publisher }}</span>
            </div>
          </header>
          <div class="detail-body" v-html="contentHtml" />
        </article>
      </div>
    </section>

    <section class="cta-band">
      <div class="container cta-inner">
        <div class="cta-left">
          <h2 class="cta-title">AESTHETICS PRACTITIONER?</h2>
          <p class="cta-sub">
            Grow your business with DOSE and access new patients, forms, diary, reminders<br />
            and no show/late cancellation protection.
          </p>
        </div>
        <div class="cta-right">
          <button class="cta-btn" type="button" @click="goAbout">LEARN MORE</button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Footer from '../components/footer.vue'
import Top from '../components/top.vue'
import { fetchPublicNewsDetail } from '../api/news'
import { resolveAssetUrl, resolveHtmlAssetUrls } from '../utils/asset'

export default {
  name: 'NewsDetailPage',
  components: {
    Footer,
    Top,
  },
  data() {
    return {
      loading: true,
      errorMsg: '',
      article: null,
      defaultCover: require('../assets/images/newImg.jpg'),
      docTitleBackup: '',
    }
  },
  computed: {
    newsId() {
      const id = this.$route.params && this.$route.params.id
      return id != null && id !== '' ? String(id) : ''
    },
    pageHeading() {
      if (this.loading) return 'NEWS'
      if (!this.article) return 'NEWS'
      const seo = this.article.seoTitle != null ? String(this.article.seoTitle).trim() : ''
      if (seo) return seo
      const t = this.article.title != null ? String(this.article.title).trim() : ''
      return t || 'NEWS'
    },
    heroSeoDescription() {
      if (!this.article) return ''
      const d = this.article.seoDescription
      return d != null && String(d).trim() ? String(d).trim() : ''
    },
    coverSrc() {
      if (!this.article) return ''
      const url = this.article.coverImageUrl || this.article.cover_image_url
      const resolved = resolveAssetUrl(url)
      return resolved || this.defaultCover
    },
    contentHtml() {
      if (!this.article || !this.article.content) return ''
      return resolveHtmlAssetUrls(this.article.content)
    },
    metaDate() {
      if (!this.article) return ''
      const d = this.article.publishedAt || this.article.published_at
      if (!d) return ''
      return this.formatPublished(d)
    },
  },
  created() {
    if (typeof document !== 'undefined') {
      this.docTitleBackup = document.title
    }
  },
  beforeDestroy() {
    if (typeof document !== 'undefined' && this.docTitleBackup != null) {
      document.title = this.docTitleBackup
    }
    const m = typeof document !== 'undefined' ? document.querySelector('meta[name="description"][data-seo-managed]') : null
    if (m && m.parentNode) m.parentNode.removeChild(m)
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        this.loadArticle()
      },
    },
  },
  methods: {
    applySeo(row) {
      if (typeof document === 'undefined') return
      const t = row.seoTitle != null && String(row.seoTitle).trim() ? String(row.seoTitle).trim() : ''
      document.title = t || (row.title != null ? String(row.title).trim() : '') || this.docTitleBackup
      const desc = row.seoDescription != null ? String(row.seoDescription).trim() : ''
      let m = document.querySelector('meta[name="description"][data-seo-managed]')
      if (desc) {
        if (!m) {
          m = document.createElement('meta')
          m.setAttribute('name', 'description')
          m.setAttribute('data-seo-managed', '1')
          document.head.appendChild(m)
        }
        m.setAttribute('content', desc)
      } else if (m && m.parentNode) {
        m.parentNode.removeChild(m)
      }
    },
    formatPublished(value) {
      if (value == null) return ''
      if (typeof value === 'string') {
        const s = value.trim()
        return s.length >= 10 ? s.slice(0, 10) : s
      }
      try {
        const d = new Date(value)
        if (Number.isNaN(d.getTime())) return String(value)
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${day}`
      } catch {
        return String(value)
      }
    },
    loadArticle() {
      const id = this.newsId
      if (!id) {
        this.loading = false
        this.errorMsg = 'Invalid news link'
        this.article = null
        return
      }
      this.loading = true
      this.errorMsg = ''
      this.article = null
      fetchPublicNewsDetail(id)
        .then((row) => {
          this.article = row
          if (!row) this.errorMsg = 'Article not found'
          else this.applySeo(row)
        })
        .catch((e) => {
          this.errorMsg = (e && e.message) || 'Failed to load article'
          this.article = null
        })
        .finally(() => {
          this.loading = false
        })
    },
    goAbout() {
      this.$router.push({ name: 'about' }).catch((err) => {
        if (err && err.name !== 'NavigationDuplicated') throw err
      })
    },
  },
}
</script>

<style scoped>
.news-detail-page {
  min-height: 100vh;
  background: #ffffff;
  color: #0c0b06;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-hero {
  background: #f4f3ef;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.page-hero-inner {
  min-height: 110px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 0;
  gap: 12px;
}

.page-hero-title {
  margin: 0;
  font-size: clamp(14px, 2vw, 16px);
  letter-spacing: 0.12em;
  font-weight: 500;
  color: #0c0b06;
  text-align: center;
  line-height: 1.4;
  max-width: 900px;
  text-transform: uppercase;
}

.hero-seo-desc {
  margin: 0;
  max-width: 720px;
  font-size: 13px;
  line-height: 1.65;
  color: #0c0b06;
  text-align: center;
}

.detail-inner {
  padding: 40px 0 56px;
}

.state-msg {
  text-align: center;
  padding: 48px 16px;
  font-size: 14px;
  color: #0c0b06;
}

.state-err {
  color: #b45309;
}

.detail-article {
  max-width: 760px;
  margin: 0 auto;
}

.detail-cover {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f3f3f3;
  overflow: hidden;
  margin-bottom: 28px;
}

.cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.detail-header {
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.detail-title {
  margin: 0 0 12px;
  font-size: clamp(20px, 2.4vw, 28px);
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #0c0b06;
  line-height: 1.35;
}

.detail-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  font-size: 12px;
  letter-spacing: 0.04em;
  color: #0c0b06;
}

.meta-sep {
  color: rgba(0, 0, 0, 0.28);
  user-select: none;
}

.detail-body {
  font-size: 15px;
  line-height: 1.85;
  color: #0c0b06;
}

.detail-body >>> p {
  margin: 0 0 1em;
}

.detail-body >>> img {
  max-width: 100%;
  height: auto;
}

.cta-band {
  padding: 44px 0;
  background: #f4f3ef;
}

.cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
}

.cta-title {
  margin: 0;
  font-size: 18px;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.cta-sub {
  margin: 10px 0 0;
  color: #0c0b06;
  font-size: 12px;
  line-height: 1.7;
}

.cta-btn {
  height: 36px;
  min-width: 150px;
  padding: 0 26px;
  border: none;
  border-radius: 0;
  background: #b08a4a;
  color: #fff;
  font-size: 11px;
  letter-spacing: 0.14em;
  cursor: pointer;
}

.cta-btn:hover {
  filter: brightness(0.96);
}

@media (max-width: 720px) {
  .container {
    padding: 0 12px;
  }

  .detail-inner {
    padding: 28px 0 40px;
  }

  .cta-inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .cta-btn {
    width: 100%;
    max-width: 220px;
  }
}
</style>
